<template>
  <div class="floor-plan">
    <div class="flp-action">
      <div class="flp-edit-switch">
        <CommonSwitch
            v-if="checkPermissions()"
            v-model="editMode"
            :label="$t('field.loc_floor_plan.update_floor_plan')"
            @change="enableEditMode"
        />
      </div>
      <div class="flp-select">
        <label>{{ $t("field.loc_floor_plan.cp_loc") }}</label>
        <CommonSelect
            v-model="selectedLoc"
            :items="cpLocs"
            key-field="loc_id"
            value-field="loc_id"
            label-field="address"
            :placeholder="$t('label.select_station')"
            @change="chooseLoc"
        />
      </div>
      <div v-if="editMode" class="flp-select">
        <label>{{ $t("field.loc_floor_plan.cp_zone") }}</label>
        <CommonSelect
            v-model="selectedZone"
            :items="cpZones"
            key-field="zone_id"
            value-field="zone_id"
            label-field="name_chi"
            :placeholder="$t('label.select_zone')"
            @change="chooseZone"
        />
      </div>
      <div v-if="editMode" class="charger-icon">
        <el-radio class="charger-icon-radio" v-model="chargerIcon" :label="1">
          <ChargerIcon2 color="#626262" width="28"/>
        </el-radio>
        <el-radio v-model="chargerIcon" :label="2">
          <ChargerIcon3 color="#626262" width="28"/>
        </el-radio>
      </div>
      <div v-if="editMode" class="cp-size-change">
        <label>{{ $t("label.resize_the_charger") }}</label>
        <vue-slider :min="25" :max="60" :interval="5" class="cp-slider" v-model="cp_size"></vue-slider>
      </div>
      <div v-if="editMode" class="cp-save">
        <CommonButton :disabled="!imgUrl || loading" type="outline" @click="changeFloorPlanImage">{{
            $t("label.change_floor_plan")
          }}
        </CommonButton>
        <CommonButton class="has-loading" :loading="loading" :disabled="!hasUpdate && !imgKey" @click="save">
          {{ $t("label.save") }}
        </CommonButton>
      </div>
    </div>
    <div style="position: relative">
      <div class="flp-cps" ref="cp_dropzone">
        <DropHereIcon class="drag-icon" color="#909399" :width="50"/>
        <el-row class="flp-row" :gutter="10" v-if="editMode">
          <el-col class="cp-col" :xs="8" :sm="6" :md="4" :lg="2" v-for="cp in cps" :key="cp.cp_id">
            <div class="cp-card-main">
              <div class="cp-card cp-zone-not-dropped" :data-cp-id="cp.cp_id"
                   :class="{'cp-zone-dropped': cp.is_in_floor_plan}">
                <ChargerIcon2 v-if="chargerIcon === 1" color="#626262" :width="cp_size"/>
                <ChargerIcon3 v-else color="#626262" :width="cp_size"/>
                <span class="cp-no">{{ cp.cp_no }}</span>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="floor-map-main">
      <div v-if="imgUrl" class="cp-status-note" :style="{
        width: imgWidth + 'px',
      }">
        <div v-for="(cpStatus) in cpStatusList" :key="cpStatus" :class="{[cpStatus]: true}">
          <DotIcon width="10"/>
          <span>{{ $t(`dashboard.status.${cpStatus}`) }}</span>
        </div>
      </div>
      <div class="flp-map" ref="dropzone"
           :style="selectedLoc && imgUrl ? {
          backgroundImage: 'url(' + imgUrl + ')',
          width: imgWidth + 'px',
          height: imgHeight + 'px'
        } : {}">
        <div v-for="(cp) in cps.filter(info => info.is_in_floor_plan)"
             :key="cp.cp_id"
             class="cp-card floor-zone-dropped"
             :class="{[cp.charging_status]: true}"
             :style="{ left: Number(cp.floor_plan_x) + 'px', top: Number(cp.floor_plan_y) + 'px', position: 'absolute' }"
             :data-cp-id="cp.cp_id"
             v-if="imgUrl"
             @click="!editMode ? goToDetail(cp.cp_id) : null"
        >
          <ChargerIcon2 v-if="chargerIcon === 1" color="#626262" :width="cp_size"/>
          <ChargerIcon3 v-else color="#626262" :width="cp_size"/>
          <span class="cp-no">{{ cp.cp_no }}</span>
        </div>
        <div v-if="!selectedLoc">
          <h3>{{ $t("label.please_select_station") }}</h3>
        </div>
        <div style="min-width: 200px; height: 50%; margin-bottom: 30px" v-if="selectedLoc && !imgUrl && editMode">
          <CpFloorPlanUpload :loc-id="selectedLoc" :is-change="isChangeImage" @upload-done="onUploadDone"
                             @cancel-change-floor-plan="handleCancelChangeFloorPlanImg"/>
        </div>
        <div class="no-floor-plan" v-if="selectedLoc && !imgUrl && !editMode">
          <h3>{{ $t("label.no_floor_plan_available_title") }}</h3>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import CommonSelect from "@/components/selects/CommonSelect.vue";
import CommonSwitch from "@/components/switch/CommonSwitch.vue";
import ChargerIcon from "@/assets/icons/ChargerIcon.vue";
import ChargerIcon2 from "@/assets/icons/ChargerIcon-2.vue";
import ChargerIcon3 from "@/assets/icons/ChargerIcon-3.vue";
import DropHereIcon from "@/assets/icons/DropHereIcon.vue";
import DotIcon from "@/assets/icons/DotIcon.vue";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import interact from 'interactjs'
import CommonButton from "@/components/buttons/CommonButton.vue";
import CpFloorPlanUpload from '@/views/custom/floor-plan/CpFloorPlanUpload.vue';
import {mapState} from "vuex";

export default {
  name: 'ChargingStationMap',
  components: {
    CommonButton,
    Loading,
    CommonSelect,
    CommonSwitch,
    VueSlider,
    ChargerIcon,
    ChargerIcon2,
    ChargerIcon3,
    DropHereIcon,
    CpFloorPlanUpload,
    DotIcon
  },
  data() {
    return {
      loading: false,
      imgUrl: null,
      imgKey: null,
      chargerIcon: 1,
      imgWidth: 0,
      imgHeight: 0,
      cpDropH: 0,
      imgLoaded: false,
      selectedLoc: null,
      selectedZone: null,
      editMode: null,
      cp_size: 50,
      cpLocs: [],
      cpZones: [],
      draggable: null,
      hasUpdate: false,
      cps: [],
      allCps: [],
      isChangeImage: false,
      cpStatusList: ['Offline', 'Charging', 'Unavailable', 'Available']
    }
  },
  mounted() {
    this.getListCpLoc();
    if (this.imgUrl) {
      this.loadImage(this.imgUrl);
    }
    this.initDropDraggable();
  },
  computed: {
    ...mapState({
      permissions: state => state.user.user_info.permissions
    }),
  },
  methods: {
    checkPermissions() {
      const hasPermission = this.permissions.includes('EDIT_CP_FLOOR');
      return hasPermission;
    },
    handleCancelChangeFloorPlanImg() {
      this.changeFloorPlanImage(false);
    },
    onUploadDone(info) {
      this.loadImage(info.path, info.relative_path);
    },
    updateUrlParams(station) {
      const params = new URLSearchParams(window.location.search);
      params.set('station', station);
      window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
    },
    loadImage(url, imgKey = null, enableEditMode = true) {
      if (url) {
        const img = new window.Image();
        img.onload = () => {
          const w = img['naturalWidth'];
          const h = img['naturalHeight'];
          if (w > 1000) {
            this.imgWidth = 1000;
            this.imgHeight = (h * 1000) / w;
          } else {
            this.imgWidth = w;
            this.imgHeight = h;
          }
          this.imgLoaded = true;
        }
        img.src = url;
        if (this.editMode && enableEditMode) {
          this.draggable.draggable(true);
        }
      } else {
        this.draggable.draggable(false);
      }
      this.imgUrl = url;
      if (imgKey) this.imgKey = imgKey;
    },
    goToDetail(cpId) {
      this.$router.push(`/cps/${cpId}`)
    },
    async createCpFloor(imgKey, locId) {
      try {
        await this.$axios.post(`/cp_locs/floor-plan`, {
          "floor_plan_path": imgKey,
          "loc_id": Number(locId),
          "item_size": String(this.cp_size),
          "item_type": String(this.chargerIcon),
        })
      } catch (e) {
        console.error(e.message);
        this.$message({
          message: this.$t("error.generic"),
          type: 'error'
        });
      }
    },
    async updateCpFloor(floorInfo) {
      try {
        await this.$axios.put(`/cp_locs/floor-plan`, {
          ...floorInfo,
          "item_size": String(this.cp_size),
          "item_type": String(this.chargerIcon)
        });
      } catch (e) {
        console.error(e.message);
        this.$message({
          message: this.$t("error.generic"),
          type: 'error'
        });
      }
    },
    async updateFloorPlanCpsCoordinate(cps) {
      try {
        const updateCps = cps.map(cp => {
          return {
            "cp_id": cp.cp_id,
            "floor_plan_x": cp.floor_plan_x || null,
            "floor_plan_y": cp.floor_plan_y || null,
          }
        });
        if (updateCps.length > 0) {
          await this.$axios.put(`/cp_locs/floor-plan/update-cps-coordinate`, updateCps);
        }
      } catch (e) {
        console.error(e.message);
        this.$message({
          message: this.$t("error.generic"),
          type: 'error'
        });
      }
    },
    async save() {
      try {
        this.loading = true;
        const matchLoc = this.cpLocs.find(cpLoc => cpLoc.loc_id === Number(this.selectedLoc));
        if (matchLoc && !matchLoc.cp_floor && this.imgKey) await this.createCpFloor(this.imgKey, this.selectedLoc);
        else if (matchLoc && matchLoc.cp_floor && (this.imgKey || Number(matchLoc.cp_floor['item_size'] || 0) !== this.cp_size || Number(matchLoc.cp_floor['item_type'] || 0) !== this.chargerIcon)) await this.updateCpFloor({
          ...matchLoc.cp_floor,
          floor_plan_path: this.imgKey || matchLoc.cp_floor.floor_plan_path,
        });
        await this.updateFloorPlanCpsCoordinate(this.cps.filter(cp => cp.is_updated));
        this.$message({
          message: this.$t("message.save_successfully"),
          type: 'success'
        });
        if (this.hasUpdate) this.hasUpdate = false;
        if (this.imgKey) this.imgKey = null;
      } catch (e) {
      } finally {
        this.loading = false;
      }
    },
    async changeFloorPlanImage(status) {
      if (status) {
        this.loadImage(null);
        this.isChangeImage = true;
      } else {
        const selectedCpLoc = this.cpLocs.find(cpLoc => cpLoc.loc_id === Number(this.selectedLoc));
        if (selectedCpLoc) {
          this.loadImage(selectedCpLoc['cp_floor'] ? selectedCpLoc['cp_floor']['floor_plan_path_view'] : null, null, false);
        }
        this.imgKey = null;
      }
    },
    enableEditMode(status) {
      if (status) {
        this.draggable.draggable(!!this.imgUrl);
      } else this.draggable.draggable(status);
    },
    async getListCpLoc() {
      try {
        const res = await this.$axios.get('/cp_locs');
        this.cpLocs = res.data?.response?.dataset || [];
        if (this.cpLocs.length > 0) {
          if (this.$route.query.station) {
            await this.chooseLoc(this.$route.query.station, true);
          }
        }
      } catch (error) {
        console.error(error);
        this.$message({
          message: this.$t("error.generic"),
          type: 'error'
        });
      }
    },
    async getListCpZoneByLocId(locId) {
      try {
        if (!locId || locId === "") {
          this.cpZones = [];
          this.cps = [];
          return;
        }
        const res = await this.$axios.get(`/cp_zones?loc_id=${locId}`);
        this.cpZones = res.data?.response?.dataset || [];
        if (this.cpZones.length > 0) {
          await this.getCpByZoneId(locId, -1, true);
        } else {
          this.cps = [];
          this.allCps = [];
        }
        if (this.cpZones.length > 1) {
          this.cpZones.unshift({
            name_chi: this.$t('label.all'),
            zone_id: -1,
          });
          setTimeout(async () => {
            this.selectedZone = -1;
          }, 100)
        } else if (this.cpZones.length === 1) {
          setTimeout(async () => {
            this.selectedZone = this.cpZones[0].zone_id;
          }, 100)

        }
      } catch (error) {
        console.error(error);
        this.$message({
          message: this.$t("error.generic"),
          type: 'error'
        });
      }
    },
    async getCpByZoneId(locId, zoneId, isAuto) {
      if (isAuto) {
        try {
          const res = await this.$axios.get(`/cps?loc_id=${locId}`);
          const cps = (res.data?.response?.dataset || []);
          this.cps = cps.map(cp => {
            cp.is_in_floor_plan = !!(cp.floor_plan_x && cp.floor_plan_y && Number(cp.floor_plan_x) > 0 && Number(cp.floor_plan_y) > 0);
            return cp;
          });
          this.allCps = this.cps;
        } catch (e) {
          console.error(e);
          this.$message({
            message: this.$t("error.generic"),
            type: 'error'
          });
        }
      } else {
        let cps = this.allCps;
        if (!zoneId || zoneId === "") {
          cps = []
        } else if (zoneId && zoneId !== "-1") {
          cps = this.allCps.filter(cp => cp.zone_id === Number(zoneId));
        }
        const updatedCps = this.cps.filter(cp => cp.is_updated);
        updatedCps.forEach(updatedCp => {
          const match = cps.find(cp => cp.cp_id === updatedCp.cp_id);
          if (match) {
            match.is_in_floor_plan = updatedCp.is_in_floor_plan;
            match.floor_plan_x = updatedCp.floor_plan_x;
            match.floor_plan_y = updatedCp.floor_plan_y;
            match.is_updated = true;
          } else {
            cps.push(updatedCp);
          }
        })
        this.cps = cps;
      }
    },
    async chooseLoc(loc, onload) {
      if (onload) {
        setTimeout(() => {
          this.selectedLoc = loc;
        }, 100)
      } else {
        this.selectedLoc = loc;
        this.updateUrlParams(loc);
      }
      const selectedCpLoc = this.cpLocs.find(cpLoc => cpLoc.loc_id === Number(loc));
      if (selectedCpLoc) {
        this.loadImage(selectedCpLoc['cp_floor'] ? selectedCpLoc['cp_floor']['floor_plan_path_view'] : null);
        if (selectedCpLoc['cp_floor']) {
          if (selectedCpLoc['cp_floor']['item_size']) {
            this.cp_size = Number(selectedCpLoc['cp_floor']['item_size']);
          }
          if (selectedCpLoc['cp_floor']['item_type']) {
            this.chargerIcon = Number(selectedCpLoc['cp_floor']['item_type']);
          }
        }
      }
      await this.getListCpZoneByLocId(loc);
    },
    async chooseZone(zone) {
      this.selectedZone = zone;
      if (zone) await this.getCpByZoneId(this.selectedLoc, this.selectedZone);
      else this.cps = [];
    },
    initDropDraggable() {
      let dragData = {};
      let draggedElement = null;
      this.draggable = interact('.cp-card:not(.cp-zone-dropped)').draggable({
        inertia: true,
        modifiers: [],
        autoScroll: true,
        listeners: {
          start(event) {
            draggedElement = event.target;
            draggedElement.style.opacity = '0.5';
            draggedElement.classList.add('dragging');
          },
          move(event) {
            const target = event.target;
            let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
            let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;
            target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute('data-x', x);
            target.setAttribute('data-y', y);
            dragData = {card: target, x, y};
          },
          end(event) {
            event.target.style.opacity = '';
            event.target.classList.remove('dragging');
            if (event.target.classList.contains('floor-zone-dropped')) {
              if (!event.target.parentElement.classList.contains('cp-col')) {
                event.target.style.transform = '';
                event.target.setAttribute('data-x', 0)
                event.target.setAttribute('data-y', 0)
              }
            } else {
              if (!event.target.parentElement.classList.contains('flp-map')) {
                event.target.style.transform = '';
                event.target.setAttribute('data-x', 0)
                event.target.setAttribute('data-y', 0)
              }
            }
          }
        }
      }).draggable(false);
      interact(this.$refs.cp_dropzone)
          .dropzone({
            accept: '.cp-card:not(.cp-zone-not-dropped)',
            overlap: 0.5,
            ondropactivate: function (event) {
              event.target.classList.add('target-cp-dropzone')
            },
            ondragenter(event) {
              event.target.classList.add('active-cp-dropzone')
            },
            ondragleave(event) {
              event.target.classList.remove('active-cp-dropzone')
            },
            ondrop: (event) => {
              const card = event.relatedTarget
              // card.style.transform = ''
              card.setAttribute('data-x', 0)
              card.setAttribute('data-y', 0)
              const cpId = parseInt(dragData.card.getAttribute("data-cp-id"));
              this.cps = this.cps.map(cp => {
                if (cp.cp_id === cpId) {
                  cp.is_in_floor_plan = false;
                  cp.floor_plan_x = undefined;
                  cp.floor_plan_y = undefined;
                  cp.is_updated = true;
                }
                return cp;
              });
              if (!this.hasUpdate) this.hasUpdate = true;
            },
            ondropdeactivate(event) {
              event.target.classList.remove('active-cp-dropzone');
              event.target.classList.remove('target-cp-dropzone');
            }
          });
      interact(this.$refs.dropzone)
          .dropzone({
            accept: '.cp-card',
            overlap: 0.5,
            ondropactivate: function (event) {
              event.target.classList.add('target-dropzone')
            },
            ondragenter(event) {
              event.target.classList.add('active-dropzone')
            },
            ondragleave(event) {
              event.target.classList.remove('active-dropzone')
            },
            ondrop: (event) => {
              const card = event.relatedTarget
              // card.style.transform = ''
              card.setAttribute('data-x', 0)
              card.setAttribute('data-y', 0)
              const cpId = parseInt(dragData.card.getAttribute("data-cp-id"));
              const dropRect = this.$refs.dropzone.getBoundingClientRect();
              const cardRect = card.getBoundingClientRect();
              const x = cardRect.left - dropRect.left;
              const y = cardRect.top - dropRect.top;

              const matchCp = this.cps.find(cp => cp.cp_id === cpId);
              if (
                  matchCp &&
                  (matchCp.floor_plan_x !== x || matchCp.floor_plan_y !== y)
              ) {
                this.cps = this.cps.map(cp => {
                  if (cp.cp_id === cpId) {
                    cp.floor_plan_x = x;
                    cp.floor_plan_y = y;
                    cp.is_in_floor_plan = true;
                    cp.is_updated = true;
                  }
                  return cp;
                })
                if (!this.hasUpdate) this.hasUpdate = true;
              }
            },
            ondropdeactivate(event) {
              event.target.classList.remove('active-dropzone');
              event.target.classList.remove('target-dropzone');
            }
          });
    },
  }
}
</script>
<style lang="scss">
.floor-plan {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 90px);

  .Unavailable {
    span {
      color: red;
    }

    svg {
      fill: red;
    }
  }

  .Available {
    span {
      color: #3fff00;
    }

    svg {
      fill: #3fff00;
    }
  }

  .Charging {
    span {
      color: dodgerblue;
    }

    svg {
      fill: dodgerblue;
    }
  }

  .Offline {
    span {
      color: #ccc;
    }

    svg {
      fill: #ccc;
    }
  }

  .flp-action {
    display: flex;
    flex-direction: row;
    gap: 15px;

    .flp-select {
      display: flex;
      flex-direction: row;
      gap: 5px;
      align-items: center;

      select {
        min-width: 100px;
      }
    }

    .cp-size-change {
      display: flex;
      flex-direction: row;
      gap: 15px;
      align-items: center;

      .cp-slider {
        min-width: 200px;
      }
    }

    .charger-icon {
      display: flex;
      align-items: center;
      gap: 5px;

      .charger-icon-radio {
        display: flex;
        align-items: center;
      }
    }

    .cp-save {
      display: flex;
      align-items: center;
      flex-direction: row;
      gap: 5px;

      button {
        min-width: 100px;
      }
    }
  }

  .flp-cps {
    position: relative;
    padding: 10px 0;

    .drag-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: none;
      z-index: 100;
    }

    .cp-zone-dropped {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .cp-col {
      display: flex;
      justify-content: center;
    }

    .cp-card-main {
      //position: relative;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      margin-bottom: 10px;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }

    .cp-drop-target {
      width: 70%;
      justify-content: center;
      border: 1px dashed;
      border-radius: 8px;
      margin: 0 auto;
      display: none;
    }

    .flp-row {
      padding: 10px 10px 0 10px;
      border: dashed 2px white;
      border-radius: 8px;
    }
  }

  .cp-card {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    cursor: pointer;
    user-select: none;

    //position: absolute;
    //top: 0;
    //left: 0;
    .cp-id {
      font-weight: bold;
      margin-bottom: 5px;
      padding: 5px;
    }

    .cp-no {
      color: #626262;
      font-size: 12px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .floor-map-main {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    flex-direction: column;

    .no-floor-plan {
      h3 {
        color: #909090;
      }
    }

    .cp-status-note {
      display: flex;
      flex-direction: row;
      gap: 15px;

      span {
        font-size: 14px;
        color: #1c1c1c;
        margin-left: 5px;
      }
    }
  }

  .flp-map {
    //background-color: #e6f1ff;
    position: relative;
    z-index: 1;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border: 2px dashed white;
    padding: 10px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    height: calc(100% - 4px);
    background-origin: content-box;
  }

  .active-dropzone {
    border-color: green !important;
  }

  .target-dropzone {
    border-color: #45adff;
  }

  .active-cp-dropzone {
    .cp-drop-target {
      display: flex;
    }

    .flp-row {
      border-color: green !important;
      opacity: 0.3;
    }

    .drag-icon {
      display: unset;
      fill: #0093006b !important;
    }
  }

  .target-cp-dropzone {
    .cp-drop-target {
      display: flex;
    }

    .flp-row {
      border-color: #45adff;
      opacity: 0.3;
    }

    .drag-icon {
      display: unset;
      fill: #45adff8c;
    }
  }

  .cp-card.floor-zone-dropped {
    position: absolute;
    z-index: 10;
  }

  .cp-card.dragging {
    z-index: 1001 !important;
  }

}
</style>
